import { useEffect, useState } from 'react';

import { NavigateFunction, useNavigate } from 'react-router';

import {
  TUTORIAL_ANCHORS,
  TUTORIAL_SEGMENTS,
  TUTORIAL_TAB_MOUNT_WAIT_MS,
  TUTORIAL_TABLE_LOADED_WAIT_MS,
  TUTORIAL_TAB_TRANSITION_DELAY_MS,
  dataTourSelector,
  sleep,
} from '@/constants';
import { waitForElement } from '@/utils';

import { useLatest } from './useLatest';

type BootstrapArgs = {
  navigate: NavigateFunction;
  pathname: string;
  signal: AbortSignal;
  onReady: () => void;
  onAbort: () => void;
};

const runBootstrap = async ({ navigate, pathname, signal, onReady, onAbort }: BootstrapArgs): Promise<void> => {
  const firstSegment = TUTORIAL_SEGMENTS[0];

  if (pathname !== firstSegment.route) {
    navigate(firstSegment.route);
    await sleep(TUTORIAL_TAB_TRANSITION_DELAY_MS);
  }

  if (signal.aborted) return;

  const anchor = await waitForElement(dataTourSelector(TUTORIAL_ANCHORS.airesTab), {
    timeout: TUTORIAL_TAB_MOUNT_WAIT_MS,
    signal,
  });

  if (signal.aborted) return;

  if (!anchor) {
    onAbort();
    return;
  }

  // Wait for the AIRES table to finish loading (the [data-tour="table-loaded"] marker, present once
  // isLoading is false) before starting, so the tour never opens over a loading skeleton on a slow
  // connection. Bounded, so a slow load does not block the start indefinitely.
  await waitForElement(dataTourSelector(TUTORIAL_ANCHORS.tableLoaded), {
    timeout: TUTORIAL_TABLE_LOADED_WAIT_MS,
    signal,
  });

  if (signal.aborted) return;

  onReady();
};

type Options = {
  isActive: boolean;
  stopTutorial: () => void;
};

export const useTutorialBootstrap = ({ isActive, stopTutorial }: Options) => {
  const navigateRef = useLatest(useNavigate());

  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    if (!isActive) {
      setIsRunning(false);
      return;
    }

    const controller = new AbortController();
    void runBootstrap({
      navigate: navigateRef.current,
      pathname: window.location.pathname,
      signal: controller.signal,
      onReady: () => setIsRunning(true),
      onAbort: stopTutorial,
    });

    return () => controller.abort();
  }, [isActive, stopTutorial, navigateRef]);

  return { isRunning };
};
